import { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import BeatLoader from "react-spinners/BeatLoader";
import { handleLogout } from "../config/axios";

const PasswordChangeSchema = Yup.object().shape({
  password: Yup.string()
    .min(6, "La password deve essere di almeno 6 caratteri")
    .required("Password richiesta"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Le password non corrispondono")
    .required("Conferma password richiesta"),
});

export default function UserProfile() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [extendedUserInfo, setExtendedUserInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("profile"); // "profile" o "password"

  // Carica le informazioni dell'utente
  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const userObj = JSON.parse(userStr);
        setUserInfo(userObj);
      } catch (err) {
        console.error("Errore nel parsing dei dati utente:", err);
      }
    }
  }, []);

  // Carica le informazioni estese dell'utente
  useEffect(() => {
    const fetchExtendedUserInfo = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        
        if (!token) {
          throw new Error("Token non trovato");
        }
        
        const response = await axios.get("/nodeapi/keycloak-protected", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        

           if (response.status !== 200 || response.data === 201) {
          handleLogout(response, "/nodeapi/keycloak", JSON.stringify(""));
         return;
       }
        
        setExtendedUserInfo(response.data);
      } catch (err) {
        console.error("Errore nel recupero delle informazioni estese dell'utente:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchExtendedUserInfo();
  }, []);

  const handlePasswordChange = async (values, { setSubmitting }) => {
    try {
      setError("");
      
      // Ottieni il token JWT dall'archiviazione locale
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token non trovato");
      }
      
      // Decodifica il token JWT per ottenere l'ID utente
      const tokenParts = token.split(".");
      if (tokenParts.length !== 3) {
        throw new Error("Token non valido");
      }
      
      const payload = JSON.parse(atob(tokenParts[1]));
      const userId = payload.sub;
      
      if (!userId) {
        throw new Error("ID utente non trovato nel token");
      }
      
      // Effettua la chiamata API per modificare la password
      await axios.put(
        `http://localhost:8080/admin/realms/master/users/${userId}/reset-password`,
        {
          temporary: false,
          type: "password",
          value: values.password,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      setSuccess(true);
      
      // Reindirizza alla home dopo 2 secondi
      setTimeout(() => {
        navigate("/");
      }, 2000);
      
    } catch (err) {
      console.error("Errore durante il cambio password:", err);
      setError(
        err.response?.data?.message || 
        "Si è verificato un errore durante il cambio della password. Riprova più tardi."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 mt-24">
      <div className="max-w-3xl mx-auto bg-white dark:bg-neutral-800 rounded-lg shadow-md overflow-hidden">
        {/* Header del profilo */}
        <div className="bg-blue-600 dark:bg-blue-800 text-white p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 mr-4">
              <div className="w-20 h-20 rounded-full bg-white/30 flex items-center justify-center">
                <span className="material-symbols-outlined text-4xl">person</span>
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-bold">
                {userInfo?.firstName} {userInfo?.lastName}
              </h1>
              <p className="text-blue-100">{userInfo?.email}</p>
              <p className="text-blue-100 mt-1">
                Ruolo: {userInfo?.profile || "Utente"}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-neutral-700">
          <nav className="flex">
            <button
              onClick={() => setActiveTab("profile")}
              className={`py-4 px-6 font-medium text-sm ${
                activeTab === "profile"
                  ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400"
                  : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              }`}
            >
              Profilo
            </button>
            <button
              onClick={() => setActiveTab("password")}
              className={`py-4 px-6 font-medium text-sm ${
                activeTab === "password"
                  ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400"
                  : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              }`}
            >
              Modifica Password
            </button>
          </nav>
        </div>

        {/* Contenuto del tab */}
        <div className="p-6">
          {activeTab === "profile" ? (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                  Informazioni Personali
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Nome
                    </label>
                    <div className="bg-gray-100 dark:bg-neutral-700 p-3 rounded-md">
                      {userInfo?.first_name || extendedUserInfo?.user?.firstName || "N/A"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Cognome
                    </label>
                    <div className="bg-gray-100 dark:bg-neutral-700 p-3 rounded-md">
                      {userInfo?.last_name || extendedUserInfo?.user?.lastName || "N/A"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email
                    </label>
                    <div className="bg-gray-100 dark:bg-neutral-700 p-3 rounded-md">
                      {userInfo?.email || extendedUserInfo?.user?.email || "N/A"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Username
                    </label>
                    <div className="bg-gray-100 dark:bg-neutral-700 p-3 rounded-md">
                      {userInfo?.username || extendedUserInfo?.user?.username || "N/A"}
                    </div>
                  </div>
                  {extendedUserInfo?.user?.attributes && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          ID AS400
                        </label>
                        <div className="bg-gray-100 dark:bg-neutral-700 p-3 rounded-md">
                          {extendedUserInfo?.user?.attributes?.as400_id?.[0] || "N/A"}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Stato
                        </label>
                        <div className="bg-gray-100 dark:bg-neutral-700 p-3 rounded-md">
                          {extendedUserInfo?.user?.attributes?.status?.[0] || "N/A"}
                        </div>
                      </div>
                      {extendedUserInfo?.user?.attributes?.last_psw_modify && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Ultima modifica password
                          </label>
                          <div className="bg-gray-100 dark:bg-neutral-700 p-3 rounded-md">
                            {extendedUserInfo?.user?.attributes?.last_psw_modify[0] || "N/A"}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                  Ruoli e Permessi
                </h2>
                <div className="bg-gray-100 dark:bg-neutral-700 p-4 rounded-md">
                  <div className="flex flex-wrap gap-2">
                    {extendedUserInfo?.user?.roles ? (
                      extendedUserInfo.user.roles.map((role, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                        >
                          {role}
                        </span>
                      ))
                    ) : userInfo?.roles?.map((role, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                      >
                        {role.name}
                      </span>
                    )) || "Nessun ruolo assegnato"}
                  </div>
                </div>
              </div>

              {extendedUserInfo?.user?.groupMembers && extendedUserInfo.user.groupMembers.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    Utenti nel mio gruppo
                  </h2>
                  <div className="bg-gray-100 dark:bg-neutral-700 p-4 rounded-md">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-600">
                        <thead className="bg-gray-50 dark:bg-neutral-700">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Nome
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Email
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              ID AS400
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Stato
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-neutral-800 divide-y divide-gray-200 dark:divide-neutral-600">
                          {extendedUserInfo.user.groupMembers.map((member) => (
                            <tr key={member.id}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {member.firstName} {member.lastName}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {member.username}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {member.email}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {member.attributes?.as400_id?.[0] || "N/A"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  member.attributes?.status?.[0] === 'A' 
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                }`}>
                                  {member.attributes?.status?.[0] === 'A' ? 'Attivo' : 'Inattivo'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {loading && (
                <div className="flex justify-center py-4">
                  <BeatLoader color="#3B82F6" size={10} />
                </div>
              )}
            </div>
          ) : (
            <div>
              <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                Modifica Password
              </h2>
              
              {success ? (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                  Password modificata con successo! Verrai reindirizzato alla home.
                </div>
              ) : (
                <Formik
                  initialValues={{ password: "", confirmPassword: "" }}
                  validationSchema={PasswordChangeSchema}
                  onSubmit={handlePasswordChange}
                >
                  {({ isSubmitting, errors, touched }) => (
                    <Form>
                      {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                          {error}
                        </div>
                      )}
                      
                      <div className="mb-4">
                        <label 
                          htmlFor="password" 
                          className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1"
                        >
                          Nuova Password
                        </label>
                        <Field
                          type="password"
                          name="password"
                          id="password"
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-700 dark:border-neutral-600 dark:text-white ${
                            errors.password && touched.password ? "border-red-500" : "border-neutral-300"
                          }`}
                        />
                        <ErrorMessage
                          name="password"
                          component="div"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>
                      
                      <div className="mb-6">
                        <label 
                          htmlFor="confirmPassword" 
                          className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1"
                        >
                          Conferma Password
                        </label>
                        <Field
                          type="password"
                          name="confirmPassword"
                          id="confirmPassword"
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-700 dark:border-neutral-600 dark:text-white ${
                            errors.confirmPassword && touched.confirmPassword ? "border-red-500" : "border-neutral-300"
                          }`}
                        />
                        <ErrorMessage
                          name="confirmPassword"
                          component="div"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>
                      
                      <div className="flex justify-between">
                        <button
                          type="button"
                          onClick={() => setActiveTab("profile")}
                          className="px-4 py-2 bg-neutral-200 text-neutral-700 rounded-md hover:bg-neutral-300 dark:bg-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-600"
                        >
                          Torna al Profilo
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                        >
                          {isSubmitting ? (
                            <BeatLoader color="#ffffff" size={8} />
                          ) : (
                            "Salva"
                          )}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
